import { InfiniteData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { NotificationModel } from 'api/models';
import produce from 'immer';
import groupBy from 'lodash.groupby';
import useDataQuery from 'modules/shared/hooks/useDataQuery';
import { getAllNotifications, getUnreadNotificationCount } from 'api/services';
import { capitalizeFirstLetter } from 'modules/shared/utils/text-utils';
import moment from 'moment';
import { useCallback, useMemo } from 'react';

const LIMIT = 20;

const useNotificationData = () => {
    const queryClient = useQueryClient();

    const unreadNotificationCountQuery = useDataQuery(['unReadNotificationCount'], () => getUnreadNotificationCount());

    const notificationListQuery = useInfiniteQuery(
        ['notificationList'],
        ({ pageParam = 0 }) => getAllNotifications({ offset: pageParam, limit: LIMIT }),
        {
            getNextPageParam: (lastPage, pages) => {
                const loadedNotificationsCount = pages.reduce((acc, page) => {
                    return acc + page.notifications.length;
                }, 0);

                return loadedNotificationsCount < lastPage.count ? loadedNotificationsCount : undefined;
            },
        }
    );

    const refetchQueries = useCallback(() => {
        unreadNotificationCountQuery.refetch();
        notificationListQuery.refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateReadAll = useCallback(() => {
        queryClient.setQueryData<
            InfiniteData<{
                count: number;
                notifications: NotificationModel[];
            }>
        >(['notificationList'], (data) => {
            return produce(data, (draftData) => {
                draftData.pages.forEach((row) => {
                    row.notifications.forEach((notification) => {
                        notification.read = true;
                    });
                });
            });
        });

        unreadNotificationCountQuery.setData({ count: 0 });
    }, [queryClient, unreadNotificationCountQuery]);

    const groupedNotifications = useMemo(() => {
        if (notificationListQuery.isLoading) return [];

        const notifications: NotificationModel[] = [];
        const notificationIds: string[] = [];

        notificationListQuery.data.pages.forEach((group) => {
            // Check if notification is already added
            const notificationsToBeAdded = group.notifications.filter((row) => !notificationIds.includes(row._id));

            // Keep track of the notification ids to avoid duplication
            notificationIds.push(...group.notifications.map((n) => n._id));

            // Add notfication to the list
            notifications.push(...notificationsToBeAdded);
        });

        const notificationsMap = groupBy(notifications, (item) => {
            const date = moment(item.createdAt);

            return date.calendar(null, {
                lastDay: '[Yesterday]',
                sameDay: '[Today]',
                lastWeek: () => {
                    return '[' + capitalizeFirstLetter(moment(date).fromNow()) + ']';
                },
                sameElse: () => {
                    return '[' + capitalizeFirstLetter(moment(date).fromNow()) + ']';
                },
            });
        });

        return Object.keys(notificationsMap).map((key) => ({ key, list: notificationsMap[key] }));
    }, [notificationListQuery.isLoading, notificationListQuery.data]);

    return {
        isLoadingNotifications: notificationListQuery.isLoading,
        groupedNotifications,
        unreadNotificationCount: unreadNotificationCountQuery.isLoading ? 0 : unreadNotificationCountQuery.data.count,
        isLoadingMoreNotifications: notificationListQuery.isFetchingNextPage,
        loadMoreNotifications: notificationListQuery.fetchNextPage,
        refetchQueries,
        updateReadAll,
    };
};

export default useNotificationData;
